import { useId, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components/Icon';
import styles from '../StepPanel.module.scss';

const ACCEPT_TYPES =
  'image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo,.mov,.mp4,.webm,.avi';

export type MediaDropZoneProps = {
  readonly isUploading: boolean;
  readonly onFilesSelected: (files: FileList | null) => void;
};

/**
 * Drag-and-drop target for visualization photos and video.
 *
 * Drag depth is counted rather than toggled: `dragenter`/`dragleave` also fire
 * for the children, so a plain boolean would clear the highlight mid-hover.
 */
export function MediaDropZone({ isUploading, onFilesSelected }: MediaDropZoneProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [isDropActive, setDropActive] = useState(false);

  function handleFiles(files: FileList | null) {
    onFilesSelected(files);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <label
      className={styles.videoDropZone}
      data-active={isDropActive ? 'true' : undefined}
      htmlFor={inputId}
      onDragEnter={(event) => {
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current += 1;
        setDropActive(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current -= 1;
        if (dragDepthRef.current <= 0) {
          dragDepthRef.current = 0;
          setDropActive(false);
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current = 0;
        setDropActive(false);
        handleFiles(event.dataTransfer.files);
      }}
    >
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept={ACCEPT_TYPES}
        multiple
        className={styles.videoFileInput}
        onChange={(event: ChangeEvent<HTMLInputElement>) => handleFiles(event.target.files)}
        disabled={isUploading}
      />
      <span className={styles.videoDropIcon} aria-hidden>
        <Icon name="attach" size={28} />
      </span>
      <span className={styles.videoDropText}>
        <span className={styles.videoDropPrimary}>
          {isUploading ? t('modeling.uploading') : t('modeling.addMedia')}
        </span>
        <span className={styles.videoDropHint}>{t('modeling.dropOrBrowse')}</span>
      </span>
    </label>
  );
}
